import type { BattleConfig, MathChallenge, BattleSkill } from '../types/game';
import type { GameEngine } from '../engine/GameEngine';
import { el, button, clear, type El } from './dom';
import { MinigameView } from './MinigameView';

export interface BattleViewCallbacks {
  onVictory: () => void;
  onDefeat?: () => void;
  onSfx?: (name: string) => void;
}

/**
 * Componente de combate educativo por turnos.
 * 
 * Muestra al guardián y al jugador con barras de HP, habilidades seleccionables,
 * retos matemáticos con pizarra integrada, pistas graduales y consecuencias
 * visuales de cada acción. No tiene presión de tiempo.
 */
export class BattleView {
  private root: El<'div'> | null = null;
  private currentBattle: BattleConfig | null = null;
  private guardianHp = 0;
  private playerHp = 0;
  private maxGuardianHp = 0;
  private maxPlayerHp = 0;
  private currentPhaseIndex = 0;
  private hintsUsed = 0;
  private attempts = 0;
  private selectedSkill: BattleSkill | null = null;
  private activeChallenge: MathChallenge | null = null;
  private minigame: MinigameView | null = null;
  private unlockedSkills: Set<string> = new Set();
  private showingExample = false;
  private teaching = false;
  private lastFeedback: string | null = null;
  private challengeOptions: string[] = [];

  constructor(
    private readonly host: HTMLElement,
    private readonly engine: GameEngine,
    private readonly callbacks: BattleViewCallbacks
  ) {}

  startBattle(battleId: string): void {
    const battle = this.engine.game.battles?.[battleId];
    if (!battle) return;

    this.currentBattle = battle;
    this.guardianHp = battle.guardianHp;
    this.maxGuardianHp = battle.guardianHp;
    this.maxPlayerHp = battle.playerHp ?? 100;
    this.playerHp = this.maxPlayerHp;
    this.currentPhaseIndex = 0;
    this.hintsUsed = 0;
    this.attempts = 0;
    this.selectedSkill = null;
    this.activeChallenge = null;
    this.showingExample = false;
    this.teaching = false;
    this.lastFeedback = null;
    this.challengeOptions = [];

    this.unlockedSkills.clear();
    for (const skill of battle.skills) {
      if (!skill.unlockedIf || this.engine.evaluateCondition(skill.unlockedIf)) {
        this.unlockedSkills.add(skill.id);
      }
    }

    this.render();
    this.host.append(this.root!);
  }

  private render(): void {
    if (!this.currentBattle) return;
    this.root = el('div', { className: 'ae-battle-root' });

    const overlay = el('div', { className: 'ae-battle-overlay' });
    const panel = el('div', { className: 'ae-battle-panel' });

    panel.append(
      this.renderHeader(),
      this.renderBattleField(),
      this.renderActionBar()
    );

    overlay.append(panel);
    this.root.append(overlay);
  }

  private renderHeader(): El<'div'> {
    return el('div', { className: 'ae-battle-header' }, [
      el('h2', { className: 'ae-battle-title', textContent: `⚔ ${this.currentBattle!.guardianName}` })
    ]);
  }

  private renderBattleField(): El<'div'> {
    const field = el('div', { className: 'ae-battle-field' });

    const combatants = el('div', { className: 'ae-battle-combatants' }, [
      el('div', { className: 'ae-battle-fighter ae-battle-guardian' }, [
        el('div', { className: 'ae-battle-avatar', textContent: '🤖' }),
        el('div', { className: 'ae-battle-fighter-info' }, [
          el('div', { className: 'ae-battle-name', textContent: this.currentBattle!.guardianName }),
          this.renderHpBar(this.guardianHp, this.maxGuardianHp, 'ae-hp-guardian')
        ])
      ]),
      el('div', { className: 'ae-battle-vs', textContent: 'VS' }),
      el('div', { className: 'ae-battle-fighter ae-battle-player' }, [
        el('div', { className: 'ae-battle-avatar', textContent: '🧒' }),
        el('div', { className: 'ae-battle-fighter-info' }, [
          el('div', { className: 'ae-battle-name', textContent: 'Tú' }),
          this.renderHpBar(this.playerHp, this.maxPlayerHp, 'ae-hp-player')
        ])
      ])
    ]);
    field.append(combatants);

    const phaseInfo = this.currentBattle!.phases[this.currentPhaseIndex];
    if (phaseInfo) {
      field.append(el('div', { className: 'ae-battle-phase', textContent: phaseInfo.name }));
    }

    if (this.activeChallenge) {
      field.append(this.renderChallenge());
    }

    return field;
  }

  private renderHpBar(current: number, max: number, className: string): El<'div'> {
    const pct = Math.max(0, Math.min(100, (current / max) * 100));
    const track = el('div', { className: `ae-hp-track ${className}` });
    const fill = el('div', { className: 'ae-hp-fill', attrs: { style: `width:${pct}%` } });
    const label = el('span', { className: 'ae-hp-label', textContent: `${current}/${max}` });
    track.append(fill, label);
    return track;
  }

  private renderActionBar(): El<'div'> {
    const bar = el('div', { className: 'ae-battle-actions' });

    if (this.activeChallenge) {
      return bar;
    }

    for (const skill of this.currentBattle!.skills) {
      const unlocked = this.unlockedSkills.has(skill.id);
      const btn = button(
        `${skill.name} (${skill.operation === 'sum' ? '+' : skill.operation === 'sub' ? '−' : '✓'})`,
        () => this.selectSkill(skill),
        {
          className: `ae-battle-skill-btn ${!unlocked ? 'ae-locked' : ''}`,
          title: unlocked ? skill.description : 'Habilidad bloqueada'
        }
      );
      if (!unlocked) btn.setAttribute('disabled', 'true');
      bar.append(btn);
    }

    return bar;
  }

  private selectSkill(skill: BattleSkill): void {
    if (!this.currentBattle) return;
    this.selectedSkill = skill;
    this.hintsUsed = 0;
    this.attempts = 0;

    const phase = this.currentBattle.phases[this.currentPhaseIndex];
    if (!phase) return;

    if (phase.minigame) {
      this.openMinigame(phase.minigame, skill);
      return;
    }

    const challenge = phase.challenge ? this.currentBattle.challenges?.[phase.challenge] : undefined;
    if (!challenge) return;

    this.activeChallenge = challenge;
    this.showingExample = false;
    this.teaching = false;
    this.lastFeedback = null;
    this.challengeOptions = this.buildOptions(challenge);
    this.rerender();
  }

  /** Resuelve la fase con un minijuego del catálogo en lugar del reto clásico. */
  private openMinigame(minigameId: string, skill: BattleSkill): void {
    const spec = this.engine.game.minigames?.[minigameId];
    if (!spec) return;

    this.minigame?.destroy();
    this.minigame = new MinigameView(this.host, spec, {
      onSfx: (name) => this.callbacks.onSfx?.(name),
      onClose: (outcome) => {
        this.minigame = null;
        if (!this.currentBattle) return;
        this.selectedSkill = null;

        if (outcome.abandoned) {
          this.showMessage('El guardián espera. Puedes volver a intentarlo.', 'info');
          this.rerender();
          return;
        }
        if (outcome.success) {
          this.applyDamage(skill.power, `¡${spec.titulo ?? 'Reto'} resuelto!`);
          return;
        }
        this.showMessage('Casi. Revisa la solución y vuelve a intentarlo.', 'warning');
        this.rerender();
      }
    });
    this.minigame.open();
  }

  private renderChallenge(): El<'div'> {
    const ch = this.activeChallenge!;
    const container = el('div', { className: 'ae-battle-challenge' });

    container.append(
      el('div', { className: 'ae-challenge-problem', textContent: ch.problemText }),
      el('div', { className: 'ae-challenge-expression', textContent: ch.expression })
    );

    if (this.teaching) {
      container.append(this.renderTeaching(ch));
      return container;
    }

    if (this.showingExample && ch.demoSteps?.length) {
      const demo = el('div', { className: 'ae-challenge-demo' }, [
        el('div', { className: 'ae-demo-caption', textContent: 'Ejemplo resuelto con otros números, para ver el método:' })
      ]);
      for (const step of ch.demoSteps) {
        const stepEl = el('div', { className: 'ae-demo-step', textContent: step.text });
        if (step.highlight) stepEl.classList.add('ae-demo-highlight');
        demo.append(stepEl);
      }
      container.append(demo);
      container.append(button('Volver al reto', () => {
        this.showingExample = false;
        this.rerender();
      }, { className: 'ae-btn-primary' }));
      return container;
    }

    if (this.lastFeedback) {
      container.append(el('div', { className: 'ae-battle-message ae-msg-warning', textContent: this.lastFeedback }));
    }

    const options = el('div', { className: 'ae-challenge-options' });
    this.challengeOptions.forEach((opt, i) => {
      options.append(button(opt, () => this.answerOption(i), { className: 'ae-challenge-option' }));
    });
    container.append(options);

    if (this.hintsUsed > 0 && ch.hints?.length) {
      const hintIdx = Math.min(this.hintsUsed - 1, ch.hints.length - 1);
      container.append(el('div', { className: 'ae-challenge-hint', textContent: `💡 ${ch.hints[hintIdx]}` }));
    }

    const tools = el('div', { className: 'ae-challenge-tools' });
    if (ch.hints?.length && this.hintsUsed < ch.hints.length) {
      tools.append(button(`Pista (${this.hintsUsed}/${ch.hints.length})`, () => {
        this.hintsUsed++;
        this.rerender();
      }, { className: 'ae-btn-hint' }));
    }
    if (ch.demoSteps?.length) {
      tools.append(button('Ver ejemplo', () => {
        this.showingExample = true;
        this.rerender();
      }, { className: 'ae-btn-hint' }));
    }
    tools.append(button('Volver', () => {
      this.activeChallenge = null;
      this.selectedSkill = null;
      this.challengeOptions = [];
      this.rerender();
    }, { className: 'ae-btn-back' }));
    container.append(tools);

    return container;
  }

  /**
   * Tras agotar los intentos se explica la solución y la fase se cierra a mitad
   * de poder. Mostrar la respuesta y volver a preguntar lo mismo sólo enseña a
   * copiar, así que el reto termina aquí y el daño reducido refleja el aprendizaje.
   */
  private renderTeaching(ch: MathChallenge): El<'div'> {
    const correctIndex = ch.options?.length ? (ch.answerIndex ?? 0) : -1;
    const solution = ch.options?.length
      ? ch.options[correctIndex] ?? String(ch.answer)
      : `${ch.expression} = ${ch.answer}`;
    const box = el('div', { className: 'ae-challenge-demo ae-challenge-teaching' }, [
      el('div', { className: 'ae-demo-caption', textContent: `Así se resuelve: ${solution}` })
    ]);
    for (const step of ch.demoSteps ?? ch.hints ?? []) {
      const text = typeof step === 'string' ? step : step.text;
      box.append(el('div', { className: 'ae-demo-step', textContent: text }));
    }
    box.append(el('div', { className: 'ae-demo-step', textContent: `La respuesta era ${solution}.` }));

    const wrap = el('div', { className: 'ae-challenge-teach-wrap' }, [box]);
    wrap.append(button('Lo he entendido', () => {
      const power = Math.max(1, Math.round((this.selectedSkill?.power ?? 20) / 2));
      this.activeChallenge = null;
      this.selectedSkill = null;
      this.teaching = false;
      this.challengeOptions = [];
      this.applyDamage(power, 'El guardián acepta tu explicación. Daño reducido.');
    }, { className: 'ae-btn-primary' }));
    return wrap;
  }

  private answerOption(index: number): void {
    if (!this.activeChallenge || !this.currentBattle || !this.selectedSkill) return;
    const ch = this.activeChallenge;
    const correctIndex = ch.options?.length ? (ch.answerIndex ?? 0) : this.challengeOptions.indexOf(String(ch.answer));
    this.attempts++;

    if (index === correctIndex) {
      const damage = this.selectedSkill.power;
      const label = ch.options?.length
        ? `¡Correcto! ${ch.options[correctIndex]}`
        : `¡Correcto! ${ch.expression} = ${ch.answer}`;
      this.activeChallenge = null;
      this.selectedSkill = null;
      this.challengeOptions = [];
      this.lastFeedback = null;
      this.applyDamage(damage, label);
      return;
    }

    this.lastFeedback = 'Esa no es. Piénsalo un poco más.';
    if (this.attempts >= 3 && this.hintsUsed === 0 && ch.hints?.length) this.hintsUsed = 1;
    if (this.attempts >= 5) {
      this.teaching = true;
      this.lastFeedback = null;
    }
    this.rerender();
  }

  /**
   * Cuatro opciones: la correcta y tres distractores plausibles (errores típicos
   * de cálculo). El orden sale de una semilla fija por reto para que las opciones
   * no bailen entre rerenders.
   */
  private buildOptions(ch: MathChallenge): string[] {
    if (ch.options?.length) return ch.options;
    const a = ch.answer;
    const wrong = new Set<number>();
    const add = (n: number) => {
      if (Number.isFinite(n) && n !== a && n >= 0) wrong.add(n);
    };
    add(a + 1);
    add(a - 1);
    add(a + 10);
    add(a - 10);
    const digits = String(Math.abs(a));
    if (digits.length >= 3) {
      add(Number(digits[0] + digits[2] + digits[1] + digits.slice(3)) * Math.sign(a || 1));
    }
    if (a >= 10) add(Math.floor(a / 10));
    const picked = [...wrong].slice(0, 3);
    while (picked.length < 3) picked.push(a + picked.length + 2);
    return this.seededShuffle([String(a), ...picked.map(String)], ch.id);
  }

  private hashString(s: string): number {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  private seededShuffle<T>(items: T[], seed: string): T[] {
    let state = this.hashString(seed) || 1;
    const next = () => {
      state ^= state << 13;
      state ^= state >>> 17;
      state ^= state << 5;
      state >>>= 0;
      return state / 4294967296;
    };
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(next() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  /** Descuenta vida al guardián y decide si la fase termina. */
  private applyDamage(damage: number, message: string): void {
    if (!this.currentBattle) return;

    this.guardianHp = Math.max(0, this.guardianHp - damage);
    this.showMessage(message, 'success');

    const phase = this.currentBattle.phases[this.currentPhaseIndex];
    if (this.guardianHp <= 0 || (phase && this.guardianHp <= phase.guardianHp)) {
      this.advancePhase();
    } else {
      setTimeout(() => this.rerender(), 800);
    }
  }

  private advancePhase(): void {
    if (!this.currentBattle) return;

    const phase = this.currentBattle.phases[this.currentPhaseIndex];
    if (phase?.actions) {
      for (const action of phase.actions) {
        this.engine.executeAction(action);
      }
    }

    this.currentPhaseIndex++;

    if (this.currentPhaseIndex >= this.currentBattle.phases.length || this.guardianHp <= 0) {
      this.victory();
      return;
    }

    if (phase?.onComplete) {
      this.showMessage(phase.onComplete, 'success');
    }

    setTimeout(() => this.rerender(), 1000);
  }

  private victory(): void {
    if (!this.currentBattle) return;

    if (this.currentBattle.victoryActions) {
      for (const action of this.currentBattle.victoryActions) {
        this.engine.executeAction(action);
      }
    }

    const panel = this.root?.querySelector<HTMLElement>('.ae-battle-panel');
    if (panel) {
      clear(panel);
      panel.append(
        el('div', { className: 'ae-battle-victory' }, [
          el('h2', { textContent: '¡Victoria!' }),
          el('p', { textContent: this.currentBattle.victoryMessage ?? 'Has restaurado al guardián.' }),
          button('Continuar', () => this.close(), { className: 'ae-btn-primary' })
        ])
      );
    }

    this.callbacks.onVictory();
  }

  private showMessage(text: string, tone: 'success' | 'warning' | 'info'): void {
    const field = this.root?.querySelector('.ae-battle-field');
    if (!field) return;

    const msg = el('div', { className: `ae-battle-message ae-msg-${tone}`, textContent: text });
    field.append(msg);
    setTimeout(() => msg.remove(), 2500);
  }

  private rerender(): void {
    if (!this.root) return;
    clear(this.root);
    this.render();
    this.host.append(this.root);
  }

  close(): void {
    this.minigame?.destroy();
    this.minigame = null;
    if (this.root) {
      this.root.remove();
      this.root = null;
    }
    this.currentBattle = null;
    this.activeChallenge = null;
    this.selectedSkill = null;
  }
}
